import { type ExpenseDto, ExpenseStatus } from "@pilatestime/shared-types";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	downloadExpensesCsv,
	fetchExpenseCategoriesQueryOptions,
	fetchExpensesQueryOptions,
} from "@/api/orgMgmtCostManagement";
import { formatCurrency } from "@/components/billing/CurrencyDisplay";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import {
	getCategoryDisplayName,
	useCategoryNameResolver,
} from "@/lib/expense-category-name";
import { OrganizationManagementNavigation } from "@/routes/_auth/app/_app/org/$orgId/org-management/-components/OrganizationManagementNavigation";
import { CostManagementSubNav } from "../-components/CostManagementSubNav";
import { ExpenseFormDialog } from "../-components/ExpenseFormDialog";
import { MarkPaidDialog } from "../-components/MarkPaidDialog";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/org-management/cost-management/expenses/",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;
		context.queryClient.ensureQueryData(
			fetchExpensesQueryOptions(orgId, token, { page: 1, limit: 25 }),
		);
		context.queryClient.ensureQueryData(
			fetchExpenseCategoriesQueryOptions(orgId, token),
		);
		return { userId, token, orgId };
	},
	component: ExpensesPage,
});

function ExpensesPage() {
	const { t } = useTranslation();
	const { token, orgId } = Route.useLoaderData();
	const { locale } = useDateFormatter();
	const formatIncurred = (iso: string) =>
		new Date(`${iso}T00:00:00Z`).toLocaleDateString(locale, {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			timeZone: "UTC",
		});

	const [page, setPage] = useState(1);
	const [statusFilter, setStatusFilter] = useState<ExpenseStatus | "ALL">(
		"ALL",
	);
	const [categoryFilter, setCategoryFilter] = useState<string>("ALL");
	const [searchInput, setSearchInput] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");

	// Description filter is currently applied client-side over the visible page
	// because the API does not yet expose a search parameter — see C-13 in the
	// review doc; promote to a server-side filter when the endpoint supports it.
	useEffect(() => {
		const handle = setTimeout(
			() => setDebouncedSearch(searchInput.trim()),
			250,
		);
		return () => clearTimeout(handle);
	}, [searchInput]);

	const expensesQuery = useQuery(
		fetchExpensesQueryOptions(orgId, token, {
			page,
			limit: 25,
			status: statusFilter === "ALL" ? undefined : statusFilter,
			categoryIds: categoryFilter === "ALL" ? undefined : [categoryFilter],
		}),
	);
	const resolveCategoryName = useCategoryNameResolver(orgId, token);

	const visibleExpenses = useMemo(() => {
		const rows = expensesQuery.data?.expenses ?? [];
		if (!debouncedSearch) return rows;
		const needle = debouncedSearch.toLowerCase();
		return rows.filter((e) => {
			const haystacks = [e.description, e.vendor].filter(
				(v): v is string => !!v,
			);
			return haystacks.some((value) => value.toLowerCase().includes(needle));
		});
	}, [expensesQuery.data?.expenses, debouncedSearch]);
	const categoriesQuery = useQuery(
		fetchExpenseCategoriesQueryOptions(orgId, token),
	);

	const [createOpen, setCreateOpen] = useState(false);
	const [editTarget, setEditTarget] = useState<ExpenseDto | undefined>();
	const [markPaidTarget, setMarkPaidTarget] = useState<ExpenseDto | null>(null);

	const handleExportCsv = async () => {
		await downloadExpensesCsv(token, orgId, {
			status: statusFilter === "ALL" ? undefined : statusFilter,
			categoryIds: categoryFilter === "ALL" ? undefined : [categoryFilter],
		});
	};

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("orgManagement.costManagement.expense.pageTitle")}
				description={t(
					"orgManagement.costManagement.subtitleVariants.expenses",
				)}
				showBackButton={true}
				backButtonText={t("orgManagement.back")}
				hideOnMobile={false}
			/>
			<div className="flex-1 flex flex-col md:flex-row h-[calc(100vh-4rem)] min-h-0 overflow-hidden">
				<OrganizationManagementNavigation activeTab="cost-management" />
				<div className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto">
					<div className="space-y-4 max-w-6xl mx-auto">
						<CostManagementSubNav orgId={orgId} active="expenses" />

						<Card>
							<CardContent className="p-3 md:p-4 space-y-3">
								<div className="flex flex-col md:flex-row md:flex-wrap md:items-end md:justify-between gap-3">
									<div className="grid grid-cols-1 sm:grid-cols-2 md:flex md:flex-wrap md:items-end gap-3 md:flex-1">
										<div>
											<Label className="text-xs">
												{t("orgManagement.costManagement.filters.status")}
											</Label>
											<Select
												value={statusFilter}
												onValueChange={(v) => {
													setStatusFilter(v as ExpenseStatus | "ALL");
													setPage(1);
												}}
											>
												<SelectTrigger className="w-full md:w-40">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="ALL">
														{t("orgManagement.costManagement.filters.all")}
													</SelectItem>
													{Object.values(ExpenseStatus).map((s) => (
														<SelectItem key={s} value={s}>
															{t(
																`orgManagement.costManagement.expense.status.${s.toLowerCase()}`,
															)}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
										<div>
											<Label className="text-xs">
												{t("orgManagement.costManagement.filters.category")}
											</Label>
											<Select
												value={categoryFilter}
												onValueChange={(v) => {
													setCategoryFilter(v);
													setPage(1);
												}}
											>
												<SelectTrigger className="w-full md:w-48">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="ALL">
														{t("orgManagement.costManagement.filters.all")}
													</SelectItem>
													{(categoriesQuery.data ?? []).map((c) => (
														<SelectItem key={c.id} value={c.id}>
															{getCategoryDisplayName(c, t)}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
										<div className="sm:col-span-2 md:col-span-1 md:min-w-[220px]">
											<Label className="text-xs" htmlFor="expenses-search">
												{t("orgManagement.costManagement.expenses.searchLabel")}
											</Label>
											<Input
												id="expenses-search"
												type="search"
												value={searchInput}
												onChange={(e) => setSearchInput(e.target.value)}
												placeholder={t(
													"orgManagement.costManagement.expenses.searchPlaceholder",
												)}
												data-testid="expenses-search"
											/>
										</div>
									</div>
									<div className="flex gap-2 md:flex-none">
										<Button
											variant="outline"
											onClick={handleExportCsv}
											className="flex-1 md:flex-none"
										>
											{t("orgManagement.costManagement.csv.exportButton")}
										</Button>
										<Button
											onClick={() => setCreateOpen(true)}
											className="flex-1 md:flex-none"
										>
											{t("orgManagement.costManagement.expense.create")}
										</Button>
									</div>
								</div>

								{expensesQuery.isLoading && (
									<div className="text-sm text-base-content/60">
										{t("orgManagement.costManagement.loading")}
									</div>
								)}
								{expensesQuery.isError && (
									<div className="text-sm text-error">
										{t("orgManagement.costManagement.error")}
									</div>
								)}
								{expensesQuery.data && (
									<>
										<div className="md:hidden space-y-2">
											{visibleExpenses.map((e) => (
												<ExpenseMobileCard
													key={e.id}
													expense={e}
													incurredLabel={formatIncurred(e.incurredAt)}
													categoryName={resolveCategoryName(e.category)}
													onEdit={() => setEditTarget(e)}
													onMarkPaid={() => setMarkPaidTarget(e)}
												/>
											))}
											{visibleExpenses.length === 0 && (
												<div className="text-center py-8 text-sm text-base-content/60">
													{debouncedSearch
														? t(
																"orgManagement.costManagement.expenses.noSearchResults",
															)
														: t("orgManagement.costManagement.empty")}
												</div>
											)}
										</div>
										<div className="hidden md:block">
											<Table>
												<TableHeader>
													<TableRow>
														<TableHead>
															{t(
																"orgManagement.costManagement.expense.columns.date",
															)}
														</TableHead>
														<TableHead>
															{t(
																"orgManagement.costManagement.expense.columns.category",
															)}
														</TableHead>
														<TableHead>
															{t(
																"orgManagement.costManagement.expense.columns.description",
															)}
														</TableHead>
														<TableHead>
															{t(
																"orgManagement.costManagement.expense.columns.expected",
															)}
														</TableHead>
														<TableHead>
															{t(
																"orgManagement.costManagement.expense.columns.actual",
															)}
														</TableHead>
														<TableHead>
															{t(
																"orgManagement.costManagement.expense.columns.status",
															)}
														</TableHead>
														<TableHead>
															{t(
																"orgManagement.costManagement.expense.columns.actions",
															)}
														</TableHead>
													</TableRow>
												</TableHeader>
												<TableBody>
													{visibleExpenses.map((e) => {
														const actualNumeric = Number(e.actualAmount ?? "0");
														const isCredit = actualNumeric < 0;
														return (
															<TableRow
																key={e.id}
																className="cursor-pointer"
																onClick={() => setEditTarget(e)}
															>
																<TableCell>
																	{formatIncurred(e.incurredAt)}
																</TableCell>
																<TableCell>
																	<span className="inline-flex items-center gap-2">
																		<span
																			className="inline-block h-3 w-3 rounded-full"
																			style={{
																				backgroundColor: e.category.color,
																			}}
																		/>
																		{resolveCategoryName(e.category)}
																	</span>
																</TableCell>
																<TableCell>
																	<div className="font-medium flex items-center gap-2 flex-wrap">
																		<span>{e.description}</span>
																		{isCredit && (
																			<Badge
																				variant="info"
																				className="text-[10px]"
																			>
																				{t(
																					"orgManagement.costManagement.expenses.creditTag",
																				)}
																			</Badge>
																		)}
																	</div>
																	{e.vendor && (
																		<div className="text-xs text-base-content/60">
																			{e.vendor}
																		</div>
																	)}
																</TableCell>
																<TableCell>
																	{formatCurrency(e.expectedAmount, e.currency)}
																</TableCell>
																<TableCell
																	className={isCredit ? "text-info" : undefined}
																>
																	{e.actualAmount
																		? formatCurrency(e.actualAmount, e.currency)
																		: "—"}
																</TableCell>
																<TableCell>
																	<StatusBadge status={e.status} />
																</TableCell>
																<TableCell
																	onClick={(ev) => ev.stopPropagation()}
																>
																	{e.status === ExpenseStatus.PENDING && (
																		<Button
																			size="sm"
																			variant="outline"
																			onClick={() => setMarkPaidTarget(e)}
																		>
																			{t(
																				"orgManagement.costManagement.expense.markPaid.confirm",
																			)}
																		</Button>
																	)}
																</TableCell>
															</TableRow>
														);
													})}
													{visibleExpenses.length === 0 && (
														<TableRow>
															<TableCell
																colSpan={7}
																className="text-center py-8 text-base-content/60"
															>
																{debouncedSearch
																	? t(
																			"orgManagement.costManagement.expenses.noSearchResults",
																		)
																	: t("orgManagement.costManagement.empty")}
															</TableCell>
														</TableRow>
													)}
												</TableBody>
											</Table>
										</div>
									</>
								)}

								{expensesQuery.data &&
									expensesQuery.data.pagination.totalPages > 1 && (
										<div className="flex items-center justify-end gap-2">
											<Button
												size="sm"
												variant="outline"
												disabled={page <= 1}
												onClick={() => setPage((p) => p - 1)}
											>
												{t("pagination.previousPage")}
											</Button>
											<span className="text-xs">
												{page} / {expensesQuery.data.pagination.totalPages}
											</span>
											<Button
												size="sm"
												variant="outline"
												disabled={
													page >= expensesQuery.data.pagination.totalPages
												}
												onClick={() => setPage((p) => p + 1)}
											>
												{t("pagination.nextPage")}
											</Button>
										</div>
									)}
							</CardContent>
						</Card>
					</div>
				</div>
			</div>

			<ExpenseFormDialog
				open={createOpen}
				onOpenChange={setCreateOpen}
				token={token}
				orgId={orgId}
				categories={categoriesQuery.data ?? []}
			/>
			<ExpenseFormDialog
				open={!!editTarget}
				onOpenChange={(open) => {
					if (!open) setEditTarget(undefined);
				}}
				token={token}
				orgId={orgId}
				categories={categoriesQuery.data ?? []}
				expense={editTarget}
			/>
			<MarkPaidDialog
				open={!!markPaidTarget}
				onOpenChange={(open) => {
					if (!open) setMarkPaidTarget(null);
				}}
				token={token}
				orgId={orgId}
				expense={markPaidTarget}
			/>
		</div>
	);
}

interface ExpenseMobileCardProps {
	expense: ExpenseDto;
	incurredLabel: string;
	categoryName: string;
	onEdit: () => void;
	onMarkPaid: () => void;
}

function ExpenseMobileCard({
	expense,
	incurredLabel,
	categoryName,
	onEdit,
	onMarkPaid,
}: ExpenseMobileCardProps) {
	const { t } = useTranslation();
	const actualNumeric = Number(expense.actualAmount ?? "0");
	const isCredit = actualNumeric < 0;
	const amountToShow = expense.actualAmount ?? expense.expectedAmount;
	const hasActual = !!expense.actualAmount;

	return (
		<button
			type="button"
			onClick={onEdit}
			className="w-full text-left rounded-lg border border-base-300 bg-base-100 p-3 space-y-2 hover:bg-base-200/40 transition-colors"
		>
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0 flex-1">
					<div className="font-medium flex items-center gap-2 flex-wrap">
						<span className="truncate">{expense.description}</span>
						{isCredit && (
							<Badge variant="info" className="text-[10px]">
								{t("orgManagement.costManagement.expenses.creditTag")}
							</Badge>
						)}
					</div>
					{expense.vendor && (
						<div className="text-xs text-base-content/60 truncate">
							{expense.vendor}
						</div>
					)}
				</div>
				<StatusBadge status={expense.status} />
			</div>
			<div className="flex items-center justify-between gap-3 text-xs text-base-content/60">
				<span className="inline-flex items-center gap-1.5 truncate min-w-0">
					<span
						className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
						style={{ backgroundColor: expense.category.color }}
					/>
					<span className="truncate">{categoryName}</span>
				</span>
				<span className="tabular-nums shrink-0">{incurredLabel}</span>
			</div>
			<div className="flex items-center justify-between gap-3">
				<div>
					<div className="text-[10px] uppercase tracking-wide text-base-content/60">
						{t(
							hasActual
								? "orgManagement.costManagement.expense.columns.actual"
								: "orgManagement.costManagement.expense.columns.expected",
						)}
					</div>
					<div
						className={`text-base font-semibold tabular-nums ${isCredit ? "text-info" : "text-base-content"}`}
					>
						{formatCurrency(amountToShow, expense.currency)}
					</div>
				</div>
				{expense.status === ExpenseStatus.PENDING && (
					<Button
						size="sm"
						variant="outline"
						onClick={(ev) => {
							ev.stopPropagation();
							onMarkPaid();
						}}
					>
						{t("orgManagement.costManagement.expense.markPaid.confirm")}
					</Button>
				)}
			</div>
		</button>
	);
}

function StatusBadge({ status }: { status: ExpenseStatus }) {
	const { t } = useTranslation();
	const variant =
		status === ExpenseStatus.PAID
			? "success"
			: status === ExpenseStatus.PENDING
				? "warning"
				: "destructive";
	return (
		<Badge variant={variant}>
			{t(`orgManagement.costManagement.expense.status.${status.toLowerCase()}`)}
		</Badge>
	);
}
